/**
 * Registro do contexto de tela, fora da árvore do React.
 *
 * Precisa ser um módulo, e não um Provider: o dock vive no App (fora do
 * Router, para sobreviver à troca de página com um turno em andamento) e as
 * páginas que publicam contexto vivem dentro do Router. Um contexto de React
 * não atravessa essa fronteira sem envolver o App inteiro num provider que
 * re-renderiza a cada tecla digitada na tela.
 *
 * A página chama `useContextoDaTela(...)`; o dock lê com `lerContexto()` na
 * hora de enviar o turno e executa as ações com `executarAcao(...)`.
 */

import { useEffect, useRef } from "react";
import type { ContextoDeTela } from "@shared/assistente-contexto";

type Handler = (args: Record<string, unknown>) => void | Promise<void>;
type Registro = { contexto: ContextoDeTela | null; handlers: Record<string, Handler> };

/**
 * Guardamos a REF, não o valor: a tela muda a cada tecla digitada e a leitura
 * acontece só quando o turno é enviado. Assim o que vai ao modelo é sempre o
 * estado do instante do envio, sem nenhuma re-renderização no caminho.
 */
let registro: { current: Registro } | null = null;

/** O que a tela está mostrando agora, ou null se ninguém publicou. */
export function lerContexto(): ContextoDeTela | null {
  return registro?.current.contexto ?? null;
}

/**
 * Executa uma ação que a tela registrou. Devolve o que aconteceu em texto,
 * porque isso volta ao modelo como resultado da ferramenta — "não existe
 * mais" é informação útil quando a pessoa trocou de tela no meio do turno.
 */
export async function executarAcao(id: string, args: Record<string, unknown>): Promise<{ ok: boolean; detalhe: string }> {
  const handler = registro?.current.handlers[id];
  if (!handler) return { ok: false, detalhe: "A tela mudou e esta ação não está mais disponível." };
  try {
    await handler(args);
    return { ok: true, detalhe: "feito na tela" };
  } catch (err) {
    return { ok: false, detalhe: err instanceof Error ? err.message : "falhou" };
  }
}

/** A tela publica o que mostra e o que sabe fazer. */
export function useContextoDaTela(contexto: ContextoDeTela | null, handlers: Record<string, Handler> = {}) {
  const ref = useRef<Registro>({ contexto, handlers });
  ref.current = { contexto, handlers };

  useEffect(() => {
    registro = ref;
    return () => {
      if (registro === ref) registro = null;
    };
  }, []);
}

/**
 * Atalho para telas de LISTA. O que importa ali não é a linha, é o recorte:
 * que filtro está aplicado, quantos itens deram e quais são os primeiros.
 *
 * Mandar a lista inteira estouraria o teto de contexto e pagaria caro em toda
 * rodada; mandar só a contagem deixaria o assistente sem conseguir falar de
 * um item específico. A amostra resolve os dois lados — e ele tem ferramenta
 * de busca para o que não estiver aqui.
 */
export function useContextoDeLista(opts: {
  titulo: string;
  filtros?: Record<string, unknown>;
  total?: number;
  itens?: Array<Record<string, unknown>>;
  amostra?: number;
  extra?: Record<string, unknown>;
  acoes?: ContextoDeTela["acoes"];
  handlers?: Record<string, (args: Record<string, unknown>) => void | Promise<void>>;
}) {
  const amostra = opts.amostra ?? 15;
  const itens = opts.itens ?? [];
  useContextoDaTela(
    {
      titulo: opts.titulo,
      dados: {
        ...(opts.filtros && Object.keys(opts.filtros).length > 0 ? { filtros: opts.filtros } : {}),
        total: opts.total ?? itens.length,
        ...(itens.length > 0
          ? { primeiros: itens.slice(0, amostra), mostrando: Math.min(amostra, itens.length) }
          : {}),
        ...(opts.extra ?? {}),
      },
      acoes: opts.acoes,
    },
    opts.handlers ?? {},
  );
}
